import { useContext, useEffect, useMemo, useState } from 'react';
import type { GetServerSideProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { useQuery, type ApolloError } from '@apollo/client';
import { useRouter } from 'next/router';
import { GraphQLClient } from '@pebble/common';
import { ActivitySearchResult } from 'types';
import { Supplier } from 'interfaces';
import { GetSupplierSlugByIdQuery, SupplierActivitiesQuery } from 'graphql/queries';
import { reportError } from '@utils';
import { trackAction, Actions, trackPageView } from 'utils/amplitude';
import { getCategories, matchCategoryFromURL } from 'utils/categories';
import { getActivitiesOffsetAndLimit } from 'utils/getActivitiesOffsetAndLimit';
import { getImageUrl } from 'utils/getImageUrl';
import { EmbedContext, EmbedContextType } from 'context/EmbedContext';
import { isValidUUID } from 'utils/isValidUUID';
import { buildUrlAndQuery } from 'utils/buildUrlAndQuery';
import SupplierActivityList from 'components/SupplierActivityList';
import SupplierActivityListEmbed from 'components/Embed/SupplierActivityList/SupplierActivityListEmbed';
import PebblePagination from 'components/PebblePagination';
import { Center } from '@mantine/core';
import { Pebbles } from '@ui';
import { AmplitudeContext, AmplitudeContextType } from 'context/AmplitudeContext';

interface ISupplierActivitiesProps {
  error: boolean;
  rawError?: string;
  supplierSlug: string;
  page: string;
  categoryName?: string;
  categorySlug?: string;
  categoryId?: string;
}

const ITEMS_PER_PAGE = 10;

const Activities: NextPage<ISupplierActivitiesProps> = (props) => {
  const router = useRouter();
  const { page, categorySlug, supplierSlug, categoryId } = props;

  const { activityTags } = router.query;
  const getAllTags = activityTags?.toString().split(',');

  const { isEmbed, iframeLocation } = useContext(EmbedContext) as EmbedContextType;

  const { ready } = useContext(AmplitudeContext) as AmplitudeContextType;

  const [activePage, setActivePage] = useState(Number(page));
  const [totalPagesCount, setTotalPagesCount] = useState<number>(0);
  const { data, loading } = useQuery(SupplierActivitiesQuery, {
    variables: {
      skip: !supplierSlug,
      supplierSlug: supplierSlug,
      isOnline: true,
      ...(page && getActivitiesOffsetAndLimit(Number(page), ITEMS_PER_PAGE)),
      ...(categoryId && {
        categories: [categoryId],
      }),
      ...(getAllTags && {
        activityTags: getAllTags,
      }),
    },
    fetchPolicy: 'network-only',
  });

  const activities = useMemo<ActivitySearchResult[]>(() => {
    if (data?.marketplaceActivities) {
      return data.marketplaceActivities.items;
    }
    return [];
  }, [data]);

  const totalActivitiesCount = useMemo<number>(() => {
    if (data?.marketplaceActivities) {
      return data.marketplaceActivities.total;
    }
    return 0;
  }, [data]);

  const supplier = useMemo<Supplier | null>(() => {
    if (data?.marketplaceSupplierBySlug) {
      return data.marketplaceSupplierBySlug;
    }
    return null;
  }, [data]);

  const showControls = totalPagesCount > 0;

  useMemo(() => {
    const totalPages = totalActivitiesCount / ITEMS_PER_PAGE;
    setTotalPagesCount(Math.ceil(totalPages));
  }, [totalActivitiesCount]);

  const handlePageChange = (pageNum: number) => {
    setActivePage(pageNum);
    router.push({
      query: {
        page: pageNum,
        category: categorySlug,
        slug: supplier?.slug,
      },
    });

    if (isEmbed) {
      return trackAction(Actions.PAGINATION_IFRAME, {
        iframeLocation,
      });
    }
    return trackAction(Actions.PAGINATION_NAVIGATION);
  };

  useEffect(() => {
    if (isEmbed && ready) {
      if (activities?.length === 0)
        trackPageView(
          router.pathname,
          { supplierId: supplier?.id, details: 'supplier_no_results' },
          true,
        );

      trackPageView(router.pathname, { supplierId: supplier?.id }, true);
    } else if (ready) {
      trackPageView(router.pathname, { details: 'supplier-activities', supplierId: supplier?.id });
    }
  }, [router.pathname, isEmbed, supplier?.id, activities?.length, ready]);

  const pageTitle = `All Activities from ${supplier?.seoPageTitle} | Pebble`;

  if (loading) {
    return (
      <Center style={{ height: '50vh' }}>
        <Pebbles />
      </Center>
    );
  }

  return (
    <>
      <NextSeo
        title={pageTitle}
        description={supplier?.seoPageDescription}
        openGraph={{
          title: pageTitle,
          description: supplier?.seoPageDescription,
          site_name: 'Pebble Activities',
          ...(supplier?.cloudinaryImageId && {
            images: [
              {
                width: 400,
                height: 225,
                url: getImageUrl(supplier.cloudinaryImageId) || '',
                alt: supplier.name,
              },
            ],
          }),
        }}
      />

      {isEmbed ? (
        <SupplierActivityListEmbed
          {...props}
          activities={activities}
          supplier={supplier}
          totalActivitiesCount={totalActivitiesCount}
        >
          <PebblePagination
            activePage={activePage}
            showControls={showControls}
            handlePageChange={handlePageChange}
            totalPagesCount={totalPagesCount}
          />
        </SupplierActivityListEmbed>
      ) : (
        <SupplierActivityList
          {...props}
          activities={activities}
          supplier={supplier}
          totalActivitiesCount={totalActivitiesCount}
        >
          <PebblePagination
            activePage={activePage}
            showControls={showControls}
            handlePageChange={handlePageChange}
            totalPagesCount={totalPagesCount}
          />
        </SupplierActivityList>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  try {
    const { slug: supplier } = query;
    // 1. If the user uses the old url => /supplier/[uuid]/activities/...,
    // redirect to new url  => /supplier/[slug]/activities/...
    if (isValidUUID(supplier as string)) {
      const { data } = await GraphQLClient.query({
        query: GetSupplierSlugByIdQuery,
        variables: { supplierId: supplier },
        fetchPolicy: 'network-only',
      });

      const { slug: supplierSlug } = data.marketplaceSupplier;

      // Issue redirect
      const { category, slug, ...restOfQuery } = query;
      const url = category
        ? `/supplier/${supplierSlug}/activities/${category}`
        : `/supplier/${supplierSlug}/activities`;
      return {
        redirect: {
          destination: buildUrlAndQuery(url, restOfQuery),
          permanent: true, // 308 Permanent
        },
      };
    }
  } catch (e) {
    const error = e as ApolloError;

    reportError(error);
    return {
      props: {
        error: true,
        activities: [],
        rawError: JSON.stringify(error.message),
      },
    };
  }

  const { category, slug, page } = query;

  let CategoryName;
  let CategoryId;

  if (Array.isArray(category)) {
    const { categories, error: categoryRetrievalError } = await getCategories(GraphQLClient);

    if (categoryRetrievalError) {
      console.error(categoryRetrievalError);
    }

    if (categories && categories.length > 0) {
      const {
        categoryId,
        categoryName,
        error: categoryMatchError,
      } = matchCategoryFromURL(category[0], categories);

      if (categoryMatchError) {
        console.error(categoryMatchError);
        return {
          notFound: true,
        };
      } else if (categoryId && categoryName) {
        CategoryName = categoryName;
        CategoryId = categoryId;
      }
    }
  }

  try {
    return {
      props: {
        supplierSlug: slug,
        error: false,
        ...(CategoryName && {
          categoryName: CategoryName,
        }),
        ...(category && {
          categorySlug: category,
          categoryId: CategoryId,
        }),
        page: page || 1,
      },
    };
  } catch (e) {
    const error = e as ApolloError;

    reportError(error);
    return {
      props: {
        error: true,
        activities: [],
        rawError: JSON.stringify(error.message),
      },
    };
  }
};

export default Activities;
